'use client';



import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  currentLanguage: string;
}

const HeroSection = ({ currentLanguage }: HeroSectionProps) => {
  const content = {
    en: {
      badge: 'Infrastructure-Scale Sustainability',
      title: 'Sustainability & Carbon Credit Solutions for Infrastructure and Data centers',
      subtitle: 'Specialized engagement platform for large-scale sustainability transformation',
      description: 'Comprehensive ESG solutions for data centers, utilities, ports, railways, and public infrastructure projects. Achieve NetZero targets through AI-powered automation, quantum computing, and regulatory compliance with CSRD, SBTi, and GHG protocols.',
      cta1: 'Explore Solutions',
      cta2: 'Schedule Consultation',
      stats: [
        { value: '50+', label: 'Government Projects', icon: 'BuildingOfficeIcon' },
        { value: '200+', label: 'Enterprise Partners', icon: 'BuildingOffice2Icon' },
        { value: '15GW', label: 'Clean Energy Managed', icon: 'BoltIcon' },
        { value: '2030', label: 'NetZero Target Year', icon: 'CalendarIcon' }
      ]
    },
    hi: {
      badge: 'बुनियादी ढांचा स्तर की स्थिरता',
      title: 'बुनियादी ढांचा और डेटा सेंटरों के लिए स्थिरता और कार्बन क्रेडिट समाधान',
      subtitle: 'बड़े पैमाने पर स्थिरता परिवर्तन के लिए विशेष सहभागिता मंच',
      description: 'डेटा सेंटर, उपयोगिताओं, बंदरगाहों, रेलवे और सार्वजनिक बुनियादी ढांचा परियोजनाओं के लिए व्यापक ESG समाधान। AI-संचालित स्वचालन, क्वांटम कंप्यूटिंग और CSRD, SBTi और GHG प्रोटोकॉल के साथ नियामक अनुपालन के माध्यम से NetZero लक्ष्य प्राप्त करें।',
      cta1: 'समाधान देखें',
      cta2: 'परामर्श शेड्यूल करें',
      stats: [
        { value: '50+', label: 'सरकारी परियोजनाएं', icon: 'BuildingOfficeIcon' },
        { value: '200+', label: 'उद्यम भागीदार', icon: 'BuildingOffice2Icon' },
        { value: '15GW', label: 'स्वच्छ ऊर्जा प्रबंधित', icon: 'BoltIcon' },
        { value: '2030', label: 'NetZero लक्ष्य वर्ष', icon: 'CalendarIcon' }
      ]
    },
    ar: {
      badge: 'استدامة البنية التحتية على نطاق واسع',
      title: 'البنية التحتية ومراكز البيانات والحلول للبنية التحتية العامة والموانئ والمرافق والسكك الحديدية',
      subtitle: 'منصة مشاركة متخصصة للتحول الاستدامة واسعة النطاق',
      description: 'حلول ESG شاملة لمراكز البيانات والمرافق والموانئ والسكك الحديدية ومشاريع البنية التحتية العامة. حقق أهداف NetZero من خلال الأتمتة المدعومة بالذكاء الاصطناعي والحوسبة الكمومية والامتثال التنظيمي مع CSRD وSBTi وبروتوكولات GHG.',
      cta1: 'استكشف الحلول',
      cta2: 'جدولة استشارة',
      stats: [
        { value: '50+', label: 'المشاريع الحكومية', icon: 'BuildingOfficeIcon' },
        { value: '200+', label: 'شركاء المؤسسات', icon: 'BuildingOffice2Icon' },
        { value: '15GW', label: 'الطاقة النظيفة المُدارة', icon: 'BoltIcon' },
        { value: '2030', label: 'سنة هدف NetZero', icon: 'CalendarIcon' }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="relative bg-gradient-to-br from-surface via-background to-muted overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-primary/10 backdrop-blur-sm rounded-full px-6 py-2 border border-primary/20">
            <Icon name="BuildingOffice2Icon" size={20} className="text-primary" />
            <span className="text-primary font-semibold text-sm">
              {currentContent.badge}
            </span>
          </div>

          {/* Title */}
          <div className="space-y-4">
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-text-primary leading-tight max-w-4xl mx-auto">
              {currentContent.title}
            </h1>
            <p className="text-xl md:text-2xl text-primary font-semibold">
              {currentContent.subtitle}
            </p>
            <p className="text-lg text-text-secondary leading-relaxed max-w-3xl mx-auto">
              {currentContent.description}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <a
              href="#solutions"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-heading font-semibold text-base rounded-lg hover:bg-primary/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
            >
              {currentContent.cta1}
              <Icon name="ArrowRightIcon" size={20} className="ml-2" />
            </a>
            <a
              href="#consultation"
              className="inline-flex items-center justify-center px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold text-base rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
            >
              {currentContent.cta2}
            </a>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-12 max-w-5xl mx-auto">
            {currentContent.stats.map((stat, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-xl p-6 shadow-natural hover:shadow-natural-lg transition-all duration-300"
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={stat.icon as any} size={24} className="text-primary" />
                  </div>
                  <div className="font-heading font-bold text-3xl text-primary">
                    {stat.value}
                  </div>
                  <div className="text-sm text-text-secondary text-center">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;